// Account classification for `users` rows. The `users` table hosts four
// account classes — humans, plugin-declared bots (`svc-…`), user-created
// service accounts (`sva_…`), and the bootstrap system user — and
// `users.kind` is the authoritative marker: every insert path writes it and
// every "humans only" query filters on it. Scope is deliberately NOT part of
// the classification: a user-created service account's scope (today one
// organization) lives on its `service_accounts` metadata row, so future
// team/project scoping never touches `kind`. The id predicates below are the
// only code allowed to know service-account id shapes (enforced by lint);
// everything else branches on `kind` or calls these.
import { z } from 'zod';
import { CORE_IDS, type CoreId } from './ids.js';

export const USER_KINDS = [
  'human',
  'plugin_service_account',
  'user_service_account',
  'system',
] as const;
export const UserKindSchema = z.enum(USER_KINDS);
export type UserKind = z.infer<typeof UserKindSchema>;

/**
 * The singleton bootstrap user's row id (`ensureSystemUser`). A bare
 * sentinel, not a `usr_`-prefixed id, so id-shape checks can recognize it.
 */
export const SYSTEM_USER_ID = 'system';

/** Plugin-declared bot ids are `svc-<login>` — stable by construction, not generated. */
export const PLUGIN_SERVICE_ACCOUNT_ID_PREFIX = 'svc-';

const USER_SERVICE_ACCOUNT_ID_PREFIX = `${CORE_IDS.serviceAccount}_`;

/** True for plugin-declared, boot-seeded bots (`svc-…`, `seedServiceAccounts`). */
export function isPluginServiceAccountId(id: string): boolean {
  return id.startsWith(PLUGIN_SERVICE_ACCOUNT_ID_PREFIX);
}

/** True for user-created service accounts (`sva_…`, `ServiceAccountService`). */
export function isUserServiceAccountId(id: string): boolean {
  return id.startsWith(USER_SERVICE_ACCOUNT_ID_PREFIX);
}

/** True for either service-account family. */
export function isServiceAccountId(id: string): boolean {
  return isPluginServiceAccountId(id) || isUserServiceAccountId(id);
}

/** True for every non-human account class reachable by id shape. */
export function isSystemOrServiceAccountId(id: string): boolean {
  return id === SYSTEM_USER_ID || isServiceAccountId(id);
}

/** Either service-account family; both are backed 1:1 by a `users` row. */
export type ServiceAccountId =
  | CoreId<'serviceAccount'>
  | `${typeof PLUGIN_SERVICE_ACCOUNT_ID_PREFIX}${string}`;

/**
 * Anything that can act: a member (`usr_…`) or a service account — the
 * `users` id domain minus the bootstrap `system` row. Use it over a bare
 * `string` for run owners; a team/org id there would hand the session to the
 * wrong audience.
 */
type ActorIdValue = CoreId<'user'> | ServiceAccountId;

/** Actor syntax only; authentication and organization membership are separate checks. */
export const ActorIdSchema = z.custom<ActorIdValue>(
  isActorId,
  'Expected a user or service-account id',
);
export type ActorId = z.infer<typeof ActorIdSchema>;

// Compile-enforced by the build's tsc pass (test files are excluded from
// every typecheck gate): stops compiling if `ActorId` ever widens to `string`.
type MustBeNever<T extends never> = T;
export type ActorIdRejectsNonPrincipalIds = MustBeNever<
  'org-shared' extends ActorId ? 'org-shared' : never
>;

/**
 * A member (`usr_…`) — a person. The check personal-subscription gates
 * (Claude Max, Codex) hang on: only a person may spend one.
 */
export function isMemberId(value: string | null | undefined): value is CoreId<'user'> {
  return !!value && value.startsWith(`${CORE_IDS.user}_`);
}

/** Whether `value` has the shape of an {@link ActorId}. */
export function isActorId(value: unknown): value is ActorIdValue {
  return typeof value === 'string' && !!value && (isMemberId(value) || isServiceAccountId(value));
}

/**
 * Boundary parse to {@link ActorId}, throwing on anything else — prefer it
 * over an `as` cast so a team/org id fails loudly at the launch site.
 */
export function asActorId(value: string | null | undefined, context: string): ActorId {
  if (!isActorId(value)) {
    throw new Error(`${context}: expected a user or service-account id, got "${value ?? 'null'}"`);
  }
  return value;
}

/**
 * The first candidate that is an {@link ActorId}, or `null` when none is —
 * a launch site's owner precedence as one flat list.
 */
export function firstActorId(...candidates: Array<string | null | undefined>): ActorId | null {
  for (const candidate of candidates) {
    if (isActorId(candidate)) return candidate;
  }
  return null;
}

/**
 * `candidate` when it is an actor, else `fallback` — how an automation
 * assumes its owner's id and falls back to its own service account. Never
 * "no actor": an unowned agent run is visible to the whole organization.
 */
export function actorIdOr(candidate: string | null | undefined, fallback: ActorId): ActorId {
  return firstActorId(candidate) ?? fallback;
}
